import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';
// @ts-ignore
import v4 from 'uuid/v4';

import { Target } from '../../../../client';
import FlagIcon from '../../../../components/FlagIcon';
import { ParentTriggererContext } from '../../../../contexts/ParentTriggererContext';
import { SportIds, type SportId } from '../../../../types/sports';
import { LineUpFormula1DefaultCar, LineUpMotoStartingLine, LineUpStartingLine } from '../../assets/svg';
import { LineUpRaceTrackBySportId } from '../../constants/ui';
import { LineUpPlayerContext } from '../../contexts/LineUpPlayerContext';
import LineUpPitStopContainer from '../LineUpPitStop/LineUpPitStopContainer';
import LineUpPlayerOddsContainer from '../LineUpPlayerOdds/LineUpPlayerOddsContainer';
import LineUpRacerInformationContainer from '../LineUpRacerInformation/LineUpRacerInformationContainer';

const FADE_TIMEOUT = 1000; // ms

const transitionClassNamePrefix = 'line-up-competitor';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  cursor: pointer;

  ${!Target.__MOBILE__
    ? css`
        font-size: 120%;
      `
    : undefined}

  width: 6em;

  &:not(:last-child) {
    margin-bottom: 2em;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;

  &.${transitionClassNamePrefix}-enter {
    opacity: 0;
  }

  &.${transitionClassNamePrefix}-enter-active {
    opacity: 1;
    transition: opacity ${FADE_TIMEOUT}ms ease-out;
  }

  &.${transitionClassNamePrefix}-exit {
    opacity: 1;
  }

  &.${transitionClassNamePrefix}-exit-active {
    opacity: 0;
    transition: opacity ${FADE_TIMEOUT}ms ease-in;
  }
`;

const CarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;

  position: relative;
`;

const DefaultCar = styled(LineUpFormula1DefaultCar)`
  height: 4em;
  width: 4em;
`;

type CarType = {
  $transform?: string;
  $margin?: string;
};

const Car = styled(FlagIcon)<CarType>`
  height: 4em;
  width: 4em;

  ${({ $transform, $margin }) => css`
    transform: ${$transform};
    margin: ${$margin};
  `}
`;

const StyledLineUpPitStopContainer = styled(LineUpPitStopContainer)`
  position: absolute;
  right: 3.25em;
  top: 3em;

  transform: translateY(-50%);
`;

type PositionIndicatorType = {
  $top?: number;
  $left?: number;
};

const PositionIndicator = styled.div<PositionIndicatorType>`
  background-color: rgba(255, 255, 255, 0.2);
  height: 1.3em;
  width: 1.3em;
  border-radius: 9999px;
  position: absolute;

  ${({ $top, $left }) => css`
    top: ${$top}em;
    left: ${$left}em;
  `}

  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PositionIndicatorText = styled.div`
  font-size: 0.9em;
  color: white;
`;

const StyledLineUpRacerInformationContainer = styled(LineUpRacerInformationContainer)`
  position: absolute;
  top: 3em;
  left: 3.75em;

  transform: translateY(-50%);
`;

const PlayerName = styled.div`
  font-size: 0.9em;
  color: white;
  text-align: center;
  white-space: nowrap;
  font-weight: normal;
`;

const MotoStartingLine = styled(LineUpMotoStartingLine)`
  position: absolute;
  width: 2.13em;

  top: 0.1em;
  left: 50%;
  transform: translate(-50%);
`;

const StartingLine = styled(LineUpStartingLine)`
  position: absolute;
  width: 5em;

  top: 0.1em;
  left: 50%;
  transform: translate(-50%);
`;

const StyledLineUpPlayerOddsContainer = styled(LineUpPlayerOddsContainer)`
  width: 50%;
  font-size: 0.65em;
  margin-top: 0.1em;
  padding: 0.2em 0;

  & .odd-button-wrapper {
    line-height: 1em;
    padding: 0.6em 0;
    font-size: 1.1em;
    min-height: 2em;
  }

  & .odds-update-indicator {
    font-size: 75%;
    right: 0.4em;
  }

  & .bet-group-outcome-odd {
    position: relative;
  }

  & .bet-group-outcome-odd-chip {
    position: absolute;
    margin-bottom: 0;
    top: 175%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & .my-match-logo-wrapper {
    left: 0;
    top: 50%;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

type LineUpRacerPropsType = {
  className?: string;
  competitorId: number;
  name?: string;
  position: number;
  imgSrc?: string;
  sportId: SportId;
};

const LineUpRacer = ({ className = '', competitorId, name, position, imgSrc, sportId }: LineUpRacerPropsType) => {
  const [parentTriggerer, setParentTriggerer] = useState(null);
  const [currentCompetitorId, setCurrentCompetitorId] = useState<number>(competitorId);
  const [currentImgSrc, setCurrentImgSrc] = useState<string | undefined>(imgSrc);
  const [currentName, setCurrentName] = useState<string | undefined>(name);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (currentCompetitorId !== undefined && competitorId !== currentCompetitorId) {
      setHasChanged(true);
      const timeoutId = setTimeout(() => {
        setHasChanged(false);
        setCurrentCompetitorId(competitorId);
        setCurrentImgSrc(imgSrc);
        setCurrentName(name);
      }, FADE_TIMEOUT);

      return () => clearTimeout(timeoutId);
    }
  }, [competitorId, currentCompetitorId, imgSrc, name]);

  const onClick = useCallback(() => {
    setParentTriggerer(v4());
  }, []);

  const forwardedValue = useMemo(() => ({ parentTriggerer }), [parentTriggerer]);

  const startingLine = useMemo(() => {
    if (sportId === SportIds.MOTO) {
      return <MotoStartingLine />;
    }
    return <StartingLine />;
  }, [sportId]);

  const styles = LineUpRaceTrackBySportId[sportId];

  const forwardedState = useMemo(
    () => ({ competitorId: currentCompetitorId, isGoalKeeper: false }),
    [currentCompetitorId],
  );

  return (
    <LineUpPlayerContext.Provider value={forwardedState}>
      <Wrapper className={className} onClick={onClick}>
        <CSSTransition in={!hasChanged} timeout={FADE_TIMEOUT} classNames={transitionClassNamePrefix}>
          <ContentWrapper>
            <CarWrapper>
              {startingLine}
              {currentImgSrc ? (
                <Car flagId={currentImgSrc} $transform={styles?.imgTransform} $margin={styles?.imgMargin} />
              ) : (
                <DefaultCar />
              )}
              <StyledLineUpPitStopContainer />
              <PositionIndicator $top={styles?.positionIndicatorTop} $left={styles?.positionIndicatorLeft}>
                <PositionIndicatorText>{position}</PositionIndicatorText>
              </PositionIndicator>
              <StyledLineUpRacerInformationContainer />
            </CarWrapper>
            <PlayerName>{currentName}</PlayerName>
            <ParentTriggererContext.Provider value={forwardedValue}>
              <StyledLineUpPlayerOddsContainer />
            </ParentTriggererContext.Provider>
          </ContentWrapper>
        </CSSTransition>
      </Wrapper>
    </LineUpPlayerContext.Provider>
  );
};

export default LineUpRacer;
